import "./styles.css";

/* O site é estático. O original era um app React no Figma Make, e só três
   coisas ali dependiam de JS de verdade — o resto era layout. Este arquivo
   reimplementa essas três, e nada além disso:

     1. o painel de telemetria do hero, que "respira" (TEMP / UMID / VPD);
     2. o banner dispensável;
     3. o estado ativo do menu conforme a seção na tela.

   O scroll suave das âncoras é CSS (`scroll-behavior`), não JS. */

/* ── 1. Painel de telemetria ──────────────────────────────────────────────────
   Atenção ao que isto é: uma DEMONSTRAÇÃO, não dado real. O site publicado no
   Figma também inventava esses números no cliente — não havia nenhuma chamada
   de rede por trás deles. Mantive o comportamento idêntico para não mudar a
   mensagem da página, mas ninguém deve ler isso como leitura de sensor.

   Se um dia virar dado real, o lugar é aqui: trocar `tick()` por um fetch e
   apagar este comentário. */
const METRICS = {
  temp: { min: 24.0, max: 27.5, decimals: 1, step: 0.4, okRange: [24.5, 26.5] },
  umid: { min: 48, max: 62, decimals: 0, step: 2, okRange: [50, 60] },
  vpd: { min: 1.35, max: 1.65, decimals: 2, step: 0.05, okRange: [1.4, 1.6] },
};

function tick(card) {
  const spec = METRICS[card.dataset.metric];
  if (!spec) return;

  const valueEl = card.querySelector("[data-metric-value]");
  const statusEl = card.querySelector("[data-metric-status]");
  if (!valueEl) return;

  const current = Number.parseFloat(valueEl.textContent) || spec.min;
  const drift = (Math.random() - 0.5) * 2 * spec.step;
  const next = Math.min(spec.max, Math.max(spec.min, current + drift));

  valueEl.textContent = next.toFixed(spec.decimals);

  if (statusEl) {
    const [lo, hi] = spec.okRange;
    const ok = next >= lo && next <= hi;
    statusEl.textContent = ok ? "OK" : "Corrigindo";
    statusEl.classList.toggle("bg-green-600", ok);
    statusEl.classList.toggle("bg-warning", !ok);
    statusEl.classList.toggle("text-white", ok);
    statusEl.classList.toggle("text-warning-foreground", !ok);
  }
}

function startTelemetry() {
  const cards = document.querySelectorAll("[data-metric]");
  if (!cards.length) return;

  // Respeita quem pediu menos movimento: os valores iniciais continuam
  // visíveis, só param de oscilar.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  let timer = null;
  const run = () => cards.forEach(tick);

  const start = () => {
    if (timer === null) timer = window.setInterval(run, 3000);
  };
  const stop = () => {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };

  // Não fica girando numa aba em segundo plano.
  document.addEventListener("visibilitychange", () =>
    document.hidden ? stop() : start(),
  );
  start();
}

/* ── 2. Banner dispensável ────────────────────────────────────────────────── */
function wireDismiss() {
  document.querySelectorAll("[data-dismiss]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const banner = btn.closest("[data-dismiss-target]") || btn.parentElement;
      if (banner) banner.remove();
    });
  });
}

/* ── 3. Item de menu ativo ────────────────────────────────────────────────────
   Puro enfeite, mas é o que o original fazia. IntersectionObserver em vez de
   listener de scroll para não rodar trabalho a cada frame. */
function wireActiveNav() {
  const links = Array.from(
    document.querySelectorAll('header nav a[href^="#"]'),
  ).filter((a) => a.getAttribute("href").length > 1);
  if (!links.length) return;

  const byId = new Map(links.map((a) => [a.getAttribute("href").slice(1), a]));
  const sections = [...byId.keys()]
    .map((id) => document.getElementById(id))
    .filter(Boolean);
  if (!sections.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((e) => e.isIntersecting)
        .forEach((e) => {
          links.forEach((a) =>
            a.classList.remove("text-foreground", "font-medium"),
          );
          const active = byId.get(e.target.id);
          if (active) active.classList.add("text-foreground", "font-medium");
        });
    },
    // Só conta como "ativa" a seção que ocupa a faixa central da tela.
    { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
  );

  sections.forEach((s) => observer.observe(s));
}


/* ── 4. Formulário de contato ─────────────────────────────────────────────────
   O original (React, no Figma) tinha submit de verdade: validava e-mail OU
   telefone, POSTava e trocava o rótulo do botão entre "Enviando…", "Recebido"
   e erro. Ao converter os CTAs em links eu tratei este botão como mais um
   mailto — e ele não era: era o submit. Isto restaura o comportamento, agora
   apontando para a nossa própria edge function em vez do backend que o Figma
   provisionava.

   As MESMAS regras rodam no servidor. A validação daqui é cortesia (erra
   rápido, sem round-trip); quem decide é a function. */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_KEY;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function contactIsValid(value) {
  const v = value.trim();
  if (EMAIL_RE.test(v)) return true;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

function wireContactForm() {
  const input = document.querySelector("[data-contact-input]");
  const button = document.querySelector("[data-contact-submit]");
  const status = document.querySelector("[data-contact-status]");
  const form = input && input.closest("form");
  if (!form || !button || !status) return;

  const label = button.textContent.trim();
  const say = (msg, isError) => {
    status.textContent = msg;
    status.classList.toggle("text-destructive", Boolean(isError));
    status.classList.toggle("text-muted-foreground", !isError);
  };

  // Fallback por e-mail. Enquanto a edge function não estiver no ar (ou se ela
  // falhar), o formulário NÃO pode virar um buraco: abre o cliente de e-mail
  // já com o que a pessoa digitou no corpo. O lead chega a um humano de um
  // jeito ou de outro.
  //
  // Isto é deliberadamente diferente de "desabilitar o botão": site
  // institucional no ar com campo morto é pior do que site com campo que
  // manda e-mail.
  const mailtoFor = (value) =>
    `mailto:contato@plante.ai?subject=${encodeURIComponent("[Agendar conversa] plante.ai")}` +
    `&body=${encodeURIComponent(`Contato: ${value}\n\nEnviado pelo formulário de plante.ai.`)}`;

  const fallbackToEmail = (value) => {
    window.location.href = mailtoFor(value);
    say("Abrimos seu e-mail para você enviar o contato.");
  };

  const backendConfigured = Boolean(SUPABASE_URL && SUPABASE_KEY);
  if (!backendConfigured) {
    console.warn("[hotsite] VITE_SUPABASE_URL/KEY ausentes — o formulário envia por e-mail.");
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const value = input.value.trim();

    if (!contactIsValid(value)) {
      say("Informe um e-mail válido ou um telefone com DDD.", true);
      input.focus();
      return;
    }

    if (!backendConfigured) {
      fallbackToEmail(value);
      return;
    }

    button.disabled = true;
    button.textContent = "Enviando...";
    say("");

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/hotsite-contact`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${SUPABASE_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contact: value }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      button.textContent = "Recebido";
      input.value = "";
      say("Recebemos seu contato. Respondemos em até 24h úteis.");
      // Volta ao estado inicial, como o original fazia.
      window.setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
        say("");
      }, 5000);
    } catch (err) {
      // Mesma regra do caso sem backend: o lead não se perde por causa de uma
      // indisponibilidade nossa.
      console.error("[hotsite] falha ao enviar o contato — caindo para e-mail", err);
      button.textContent = label;
      button.disabled = false;
      fallbackToEmail(value);
    }
  });
}

startTelemetry();
wireDismiss();
wireActiveNav();
wireContactForm();
